/**
 * Effect Stone Generator
 * Creates random effect stones that can be socketed into skills.
 * Stones are dropped by enemies and provide various skill modifiers.
 */

import { EffectStone, EffectStoneEffect } from '@shared/skills/SkillCreator';
import { ElementType } from '@shared/types';

// Type aliases for clarity
type EffectStoneType = EffectStone['type'];
type StoneRarity = EffectStone['rarity'];

const STONE_TYPES: EffectStoneType[] = ['od', 'gok', 'su', 'karanlik'];

const STONE_NAMES: Record<EffectStoneType, Record<StoneRarity, string>> = {
  od: { normal: 'Od Taşı', magic: 'Parlak Od Taşı', rare: 'Yanan Od Taşı', legendary: 'Sonsuz Ateş Taşı' },
  gok: { normal: 'Gök Taşı', magic: 'Parlak Gök Taşı', rare: 'Şimşek Gök Taşı', legendary: 'Gök Yıldırımı' },
  su: { normal: 'Su Taşı', magic: 'Parlak Su Taşı', rare: 'Buzul Su Taşı', legendary: 'Ebedi Buz Taşı' },
  karanlik: { normal: 'Karanlık Taşı', magic: 'Parlak Karanlık Taşı', rare: 'Lanetli Karanlık Taşı', legendary: 'Erlik\'in Taşı' },
};

const STONE_NAME_KEYS: Record<EffectStoneType, Record<StoneRarity, string>> = {
  od: { normal: 'stone_od_normal', magic: 'stone_od_magic', rare: 'stone_od_rare', legendary: 'stone_od_legendary' },
  gok: { normal: 'stone_gok_normal', magic: 'stone_gok_magic', rare: 'stone_gok_rare', legendary: 'stone_gok_legendary' },
  su: { normal: 'stone_su_normal', magic: 'stone_su_magic', rare: 'stone_su_rare', legendary: 'stone_su_legendary' },
  karanlik: { normal: 'stone_karanlik_normal', magic: 'stone_karanlik_magic', rare: 'stone_karanlik_rare', legendary: 'stone_karanlik_legendary' },
};

// How many modifiers and value ranges per rarity
const EFFECT_RANGES: Record<StoneRarity, { min: number; max: number; modCount: number }> = {
  normal:    { min: 2, max: 5,  modCount: 1 },
  magic:     { min: 3, max: 8,  modCount: 2 },
  rare:      { min: 5, max: 12, modCount: 3 },
  legendary: { min: 8, max: 18, modCount: 4 },
};

// Element overrides mapped to stone types
const ELEMENT_OVERRIDE_MAP: Record<EffectStoneType, ElementType> = {
  od: 'od',
  gok: 'gok',
  su: 'su',
  karanlik: 'karanlik',
};

// Possible numeric effects on a stone (matching EffectStoneEffect fields)
type NumericEffectKey =
  | 'damageBonus'
  | 'areaBonus'
  | 'cooldownReduction'
  | 'manaCostReduction'
  | 'chainCount'
  | 'lifesteal'
  | 'slow'
  | 'knockback';

const POSSIBLE_EFFECTS: NumericEffectKey[] = [
  'damageBonus',
  'areaBonus',
  'cooldownReduction',
  'manaCostReduction',
  'chainCount',
  'lifesteal',
  'slow',
  'knockback',
];

// Per-effect type caps to prevent absurd values
const EFFECT_CAPS: Record<NumericEffectKey, number> = {
  damageBonus: 35,
  areaBonus: 40,
  cooldownReduction: 30,
  manaCostReduction: 30,
  chainCount: 4,
  lifesteal: 15,
  slow: 25,
  knockback: 40,
};

// Element-themed effect weight tables
const ELEMENT_EFFECT_WEIGHTS: Record<EffectStoneType, Partial<Record<NumericEffectKey, number>>> = {
  od:  { damageBonus: 3, lifesteal: 2, areaBonus: 1, cooldownReduction: 1, manaCostReduction: 1, chainCount: 1, slow: 1, knockback: 1 },
  gok: { chainCount: 3, cooldownReduction: 3, damageBonus: 2, areaBonus: 1, manaCostReduction: 1, lifesteal: 1, slow: 1, knockback: 1 },
  su:  { slow: 3, manaCostReduction: 3, cooldownReduction: 2, areaBonus: 1, damageBonus: 1, lifesteal: 1, chainCount: 1, knockback: 1 },
  karanlik: { lifesteal: 3, manaCostReduction: 3, damageBonus: 2, areaBonus: 1, cooldownReduction: 1, chainCount: 1, slow: 1, knockback: 1 },
};

const EFFECT_DESCRIPTIONS: Record<NumericEffectKey, string> = {
  damageBonus: 'Hasar bonusu',
  areaBonus: 'Alan bonusu',
  cooldownReduction: 'Bekleme suresi azaltma',
  manaCostReduction: 'Mana maliyeti azaltma',
  chainCount: 'Zincirleme hedef sayisi',
  lifesteal: 'Can cekme',
  slow: 'Yavaslatma',
  knockback: 'Geri itme',
};

function randomRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

/**
 * Select effects using element-themed weights (no duplicates).
 */
function weightedSelect(type: EffectStoneType, count: number): NumericEffectKey[] {
  const weights = ELEMENT_EFFECT_WEIGHTS[type];
  const pool = POSSIBLE_EFFECTS.filter(e => (weights[e] ?? 0) > 0);
  const selected: NumericEffectKey[] = [];

  for (let i = 0; i < count && pool.length > 0; i++) {
    const totalWeight = pool.reduce((sum, e) => sum + (weights[e] ?? 0), 0);
    let roll = Math.random() * totalWeight;
    let picked = pool[0];
    for (const e of pool) {
      roll -= (weights[e] ?? 0);
      if (roll <= 0) {
        picked = e;
        break;
      }
    }
    selected.push(picked);
    pool.splice(pool.indexOf(picked), 1);
  }

  return selected;
}

/**
 * Generate a random effect stone.
 * @param itemLevel - The level of the source (enemy level), scales effect values
 * @param luckBonus - Extra percentage added to the rarity roll
 */
export function generateEffectStone(itemLevel: number = 1, luckBonus: number = 0): EffectStone {
  // Random type
  const type = STONE_TYPES[Math.floor(Math.random() * STONE_TYPES.length)];

  // Rarity roll (influenced by item level and luck)
  // Higher item level improves rarity chances slightly (+0.3% per level, up to +10%)
  const levelBonus = Math.min(itemLevel * 0.3, 10);
  const rarityRoll = Math.random() * 100 + luckBonus + levelBonus;
  let rarity: StoneRarity;
  if (rarityRoll > 97) {
    rarity = 'legendary';   // ~3% base (was 1%), scales with level/luck
  } else if (rarityRoll > 87) {
    rarity = 'rare';        // ~10% base
  } else if (rarityRoll > 60) {
    rarity = 'magic';       // ~27% base
  } else {
    rarity = 'normal';      // ~60% base
  }

  const range = EFFECT_RANGES[rarity];
  const name = STONE_NAMES[type][rarity];
  const nameKey = STONE_NAME_KEYS[type][rarity];

  // Build the EffectStoneEffect
  const effectId = `effect_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  const selectedEffects = weightedSelect(type, range.modCount);

  const effect: EffectStoneEffect = {
    id: effectId,
    name: name,
    description: '',
  };

  const descriptionParts: string[] = [];

  for (const effectKey of selectedEffects) {
    let value: number;

    // Logarithmic level scaling: Level 1: 1.0x, Level 10: ~1.35x, Level 20: ~1.60x, Level 50: ~1.95x
    const levelMultiplier = 1 + Math.log(1 + itemLevel * 0.1) * 0.5;

    // chainCount should be small integers (1-4)
    if (effectKey === 'chainCount') {
      value = Math.min(4, Math.max(1, Math.floor(range.min / 3) + Math.floor(Math.random() * (rarity === 'legendary' ? 3 : 2))));
    } else if (effectKey === 'knockback') {
      value = Math.floor(randomRange(range.min, range.max) * levelMultiplier);
    } else {
      value = Math.floor(randomRange(range.min, range.max) * levelMultiplier);
    }

    // Apply per-effect cap
    value = Math.min(EFFECT_CAPS[effectKey], value);

    // Assign value to the corresponding effect field
    switch (effectKey) {
      case 'damageBonus': effect.damageBonus = value; break;
      case 'areaBonus': effect.areaBonus = value; break;
      case 'cooldownReduction': effect.cooldownReduction = value; break;
      case 'manaCostReduction': effect.manaCostReduction = value; break;
      case 'chainCount': effect.chainCount = value; break;
      case 'lifesteal': effect.lifesteal = value; break;
      case 'slow': effect.slow = value; break;
      case 'knockback': effect.knockback = value; break;
    }
    descriptionParts.push(`${EFFECT_DESCRIPTIONS[effectKey]}: +${value}${effectKey === 'chainCount' || effectKey === 'knockback' ? '' : '%'}`);
  }

  // Legendary stones of matching element can also override element
  if (rarity === 'legendary' && Math.random() < 0.3) {
    effect.elementOverride = ELEMENT_OVERRIDE_MAP[type];
    descriptionParts.push(`Element: ${type}`);
  }

  // Rare+ stones can have DoT (Od stones have higher chance)
  const dotChance = type === 'od' ? 0.50 : 0.15;
  if ((rarity === 'rare' || rarity === 'legendary') && Math.random() < dotChance) {
    const dotMultiplier = 1 + Math.log(1 + itemLevel * 0.1) * 0.4;
    const dotDamage = Math.min(20, Math.floor(randomRange(range.min * 0.5, range.max * 0.8) * dotMultiplier));
    const dotDuration = rarity === 'legendary' ? 4 : 3;
    effect.dot = { damage: dotDamage, duration: dotDuration };
    descriptionParts.push(`DoT: ${dotDamage} hasar / ${dotDuration}sn`);
  }

  effect.description = descriptionParts.join(', ');

  return {
    id: `stone_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
    name,
    nameKey,
    type,
    rarity,
    effect,
  };
}

/**
 * Roll whether an effect stone should drop from a killed enemy.
 * Returns null if no stone drops, or a generated stone if it does.
 */
export function rollEffectStoneDrop(enemyLevel: number, isBoss: boolean, isElite: boolean): EffectStone | null {
  // Base drop chance: 8% normal, 20% elite, 50% boss
  let dropChance = 0.08;
  if (isElite) dropChance = 0.20;
  if (isBoss) dropChance = 0.50;

  if (Math.random() > dropChance) return null;

  const luckBonus = isBoss ? 15 : (isElite ? 5 : 0);
  return generateEffectStone(enemyLevel, luckBonus);
}

// ========================
// Stone-Skill Compatibility
// ========================

import { SkillDeliveryType } from '@shared/skills/SkillDeliveryTypes';

/** Which delivery types each stone effect is compatible with */
const EFFECT_COMPATIBILITY: Record<NumericEffectKey, SkillDeliveryType[]> = {
  damageBonus:       ['melee', 'projectile', 'aoe', 'beam', 'trap', 'totem'],
  areaBonus:         ['aoe', 'aura', 'curse', 'totem'],
  cooldownReduction: ['melee', 'projectile', 'aoe', 'beam', 'buff', 'trap', 'totem', 'curse'],
  manaCostReduction: ['melee', 'projectile', 'aoe', 'beam', 'buff', 'trap', 'totem', 'curse'],
  chainCount:        ['projectile'],
  lifesteal:         ['melee', 'projectile', 'aoe', 'beam'],
  slow:              ['melee', 'projectile', 'aoe', 'beam'],
  knockback:         ['melee', 'projectile', 'aoe'],
};

/**
 * Check if an effect stone is compatible with a given skill delivery type.
 * Element stones (od/su/gök/karanlık elemental damage) are always compatible.
 * Functional effects have specific compatible delivery types.
 */
export function isStoneCompatibleWithSkill(stone: EffectStone, deliveryType: SkillDeliveryType): boolean {
  // Element override is always compatible (adds elemental damage to any skill)
  if (stone.effect.elementOverride) return true;

  // Check each effect on the stone
  const effect = stone.effect as unknown as Record<string, unknown>;
  for (const key of POSSIBLE_EFFECTS) {
    const value = effect[key];
    if (typeof value === 'number' && value > 0) {
      const compatible = EFFECT_COMPATIBILITY[key];
      if (compatible && !compatible.includes(deliveryType)) {
        return false;
      }
    }
  }

  return true;
}

/**
 * Get list of incompatible effects for a stone + delivery type combination.
 * Returns empty array if fully compatible.
 */
export function getIncompatibleEffects(stone: EffectStone, deliveryType: SkillDeliveryType): string[] {
  const incompatible: string[] = [];
  const effect = stone.effect as unknown as Record<string, unknown>;
  for (const key of POSSIBLE_EFFECTS) {
    const value = effect[key];
    if (typeof value === 'number' && value > 0) {
      const compatible = EFFECT_COMPATIBILITY[key];
      if (compatible && !compatible.includes(deliveryType)) {
        incompatible.push(EFFECT_DESCRIPTIONS[key]);
      }
    }
  }
  return incompatible;
}
